/**
 * ADR-0223 §3.4 — a recurring meeting (`/app/collab/meetings/series/new`,
 * `/app/collab/series/:id/edit`; builders and admins): the subject, a weekday
 * and time in a timezone (defaulted to the tenant's, E2), the members, when
 * it started — and, on an existing one, when it ends. Explicit submit.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { MemberPicker, type MemberCandidate } from "../components/MemberPicker.js";
import { CollabPage, errMessage, type SeriesCard } from "../components/collab.js";

const WEEKDAYS = [
  [1, "Monday"],
  [2, "Tuesday"],
  [3, "Wednesday"],
  [4, "Thursday"],
  [5, "Friday"],
  [6, "Saturday"],
  [7, "Sunday"],
] as const;

export type SeriesForm = { name: string; purpose: string; weekday: number; start_time: string; duration_minutes: string; timezone: string; starts_on: string; ended_on: string; member_account_ids: string[] };

/** The blur checks, all on submit. Pure, for the tests. */
export function validateSeries(f: SeriesForm): Partial<Record<keyof SeriesForm, string>> {
  const e: Partial<Record<keyof SeriesForm, string>> = {};
  if (!f.name.trim()) e.name = "A name is required.";
  else if (f.name.trim().length > 120) e.name = "At most 120 characters.";
  if (f.purpose.length > 2000) e.purpose = "At most 2000 characters.";
  if (!/^\d{2}:\d{2}$/.test(f.start_time)) e.start_time = "A time, HH:MM.";
  if (f.duration_minutes && !(Number(f.duration_minutes) >= 1 && Number(f.duration_minutes) <= 600)) e.duration_minutes = "Between 1 and 600 minutes.";
  if (!f.timezone.trim()) e.timezone = "A timezone is required.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.starts_on)) e.starts_on = "A date is required.";
  if (f.ended_on && f.ended_on < f.starts_on) e.ended_on = "On or after the start.";
  return e;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function CollabSeriesEdit() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState<SeriesForm>({ name: "", purpose: "", weekday: 1, start_time: "10:00", duration_minutes: "", timezone: "", starts_on: today(), ended_on: "", member_account_ids: [] });
  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof SeriesForm, string>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [settings, dir, existing] = await Promise.all([
          apiFetch<{ timezone: string }>("/api/tenant/settings").catch(() => ({ timezone: "UTC" })),
          apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates").catch(() => ({ candidates: [] as MemberCandidate[] })),
          id ? apiFetch<{ series: SeriesCard }>(`/api/tenant/collab/series/${encodeURIComponent(id)}`) : Promise.resolve(null),
        ]);
        setCandidates(dir.candidates.filter((c) => c.kind === "user"));
        if (existing) {
          const s = existing.series;
          setForm({ name: s.name, purpose: s.purpose ?? "", weekday: s.weekday, start_time: s.start_time, duration_minutes: s.duration_minutes ? String(s.duration_minutes) : "", timezone: s.timezone, starts_on: s.starts_on, ended_on: s.ended_on ?? "", member_account_ids: s.members.map((m) => m.account_id) });
        } else {
          setForm((f) => ({ ...f, timezone: settings.timezone }));
        }
      } catch (e) {
        setError(errMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const set = <K extends keyof SeriesForm>(k: K, v: SeriesForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const check = (k: keyof SeriesForm) => setErrors((e) => ({ ...e, [k]: validateSeries(form)[k] }));

  async function submit() {
    const errs = validateSeries(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0 || busy) return;
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        purpose: form.purpose.trim() || null,
        weekday: form.weekday,
        start_time: form.start_time,
        duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
        timezone: form.timezone.trim(),
        starts_on: form.starts_on,
        ...(id ? { ended_on: form.ended_on || null } : {}),
        member_account_ids: form.member_account_ids,
      };
      if (id) await apiFetch(`/api/tenant/collab/series/${encodeURIComponent(id)}`, { method: "PATCH", body });
      else await apiFetch("/api/tenant/collab/series", { method: "POST", body });
      toast.push({ kind: "success", message: id ? "Saved." : "Meeting created." });
      navigate("/app/collab/meetings");
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <CollabPage title={id ? "Edit meeting" : "New recurring meeting"} noNewIdea crumb={<Link to="/app/collab/meetings">Meetings</Link>}>
      {error ? <ErrorState detail={error} /> : null}
      {loading && !error ? <LoadingState /> : null}
      {!loading ? (
        <div className="collabForm">
          <label className="collabLabel">
            Name
            <input className="collabInput" value={form.name} maxLength={120} onChange={(e) => set("name", e.target.value)} onBlur={() => check("name")} />
            {errors.name ? <span className="collabErr">{errors.name}</span> : null}
          </label>
          <label className="collabLabel">
            Purpose
            <textarea className="collabInput" rows={3} value={form.purpose} maxLength={2000} onChange={(e) => set("purpose", e.target.value)} onBlur={() => check("purpose")} placeholder="What the meeting is for, in a sentence" />
            {errors.purpose ? <span className="collabErr">{errors.purpose}</span> : null}
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <label className="collabLabel">
              Weekday
              <select className="collabInput" value={form.weekday} onChange={(e) => set("weekday", Number(e.target.value))}>
                {WEEKDAYS.map(([n, name]) => (
                  <option key={n} value={n}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label className="collabLabel">
              Time
              <input className="collabInput" type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} onBlur={() => check("start_time")} />
              {errors.start_time ? <span className="collabErr">{errors.start_time}</span> : null}
            </label>
            <label className="collabLabel">
              Minutes
              <input className="collabInput" inputMode="numeric" value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} onBlur={() => check("duration_minutes")} placeholder="Optional" />
              {errors.duration_minutes ? <span className="collabErr">{errors.duration_minutes}</span> : null}
            </label>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: id ? "1fr 1fr 1fr" : "1fr 1fr", gap: 10 }}>
            <label className="collabLabel">
              Timezone
              <input className="collabInput" value={form.timezone} onChange={(e) => set("timezone", e.target.value)} onBlur={() => check("timezone")} />
              {errors.timezone ? <span className="collabErr">{errors.timezone}</span> : <span className="collabHelp">The workspace's, unless this meeting keeps another clock.</span>}
            </label>
            <label className="collabLabel">
              First meeting on or after
              <input className="collabInput" type="date" value={form.starts_on} onChange={(e) => set("starts_on", e.target.value)} onBlur={() => check("starts_on")} />
              {errors.starts_on ? <span className="collabErr">{errors.starts_on}</span> : null}
            </label>
            {id ? (
              <label className="collabLabel">
                Last meeting
                <input className="collabInput" type="date" value={form.ended_on} onChange={(e) => set("ended_on", e.target.value)} onBlur={() => check("ended_on")} />
                {errors.ended_on ? <span className="collabErr">{errors.ended_on}</span> : <span className="collabHelp">Set to end the meeting; its sessions stay.</span>}
              </label>
            ) : null}
          </div>
          <div className="collabLabel">
            Members
            <MemberPicker mode="multi" allowedKinds={["user"]} candidates={candidates} selected={form.member_account_ids} onChange={(next) => set("member_account_ids", next)} placeholder="Who attends by default" />
            <span className="collabHelp">The default attendees, and the people who may record a session.</span>
          </div>
          <div className="collabPost" style={{ justifyContent: "flex-start" }}>
            <button type="button" className="collabBtn collabBtnAmber" disabled={busy} onClick={() => void submit()}>
              {id ? "Save" : "Create"}
            </button>
            <Link className="collabBtn" to="/app/collab/meetings">
              Cancel
            </Link>
          </div>
        </div>
      ) : null}
    </CollabPage>
  );
}
